// What the fan activity lists (Home's recent activity, Settings' activity log) are made of:
// purchases from the transactions feed and rows of the points ledger, as the embed builds them.

export const isDreamEntriesRow = (entry) => entry?.activity_kind === 'dream_entries';

// Dream entries rows exist only while the school's program runs.
export function visibleDreamActivity(entries, programOn) {
  return (Array.isArray(entries) ? entries : []).filter((entry) => programOn || !isDreamEntriesRow(entry));
}

export const entriesOf = (entry) => Number(entry?.entries_earned) || 0;

export function activityTime(entry) {
  const time = new Date(entry?.date || 0).getTime();
  return Number.isFinite(time) ? time : 0;
}

// A purchase from the transactions feed in the shape of an activity entry, as the embed's
// home shows it: merchant, date, points and how it was paid.
function stripeRewardTitle(transaction) {
  const raw = String(transaction?.description || transaction?.raw_descriptor || '');
  const prefix = 'Stripe reward: ';
  if (raw.startsWith(prefix)) return raw.slice(prefix.length).trim();
  return '';
}

function isStripeTransaction(transaction) {
  const ext = String(transaction?.external_txn_id || transaction?.transaction_id || '');
  const description = String(transaction?.description || transaction?.raw_descriptor || '');
  return (
    transaction?.type === 'stripe' ||
    ext.startsWith('stripe_checkout_') ||
    description.startsWith('Stripe reward:')
  );
}

function purchaseChannel(transaction) {
  if (isStripeTransaction(transaction)) return '';
  if (transaction.wildfire_merchant_id || transaction.wildfire_merchant_name) return 'Online';
  if (transaction.source === 'travel' || transaction.travel_merchant_name) return 'Travel';
  if (transaction.card_nickname) return transaction.card_nickname;
  const scheme = String(transaction.card_scheme || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  if (scheme === 'visa') return 'Visa';
  if (scheme === 'mastercard' || scheme === 'master' || scheme === 'mc') return 'Mastercard';
  if (scheme === 'amex' || scheme === 'americanexpress') return 'Amex';
  if (scheme === 'discover') return 'Discover';
  if (transaction.last4) return `•••• ${transaction.last4}`;
  return 'In store';
}

function purchaseSource(transaction) {
  const declared = String(transaction.source || '').trim().toLowerCase();
  if (declared === 'olive' || declared === 'wildfire' || declared === 'travel') return declared;
  if (transaction.wildfire_merchant_id) return 'wildfire';
  if (String(transaction.olive_merchant_id || '').trim()) return 'olive';
  if (String(transaction.travel_merchant_name || '').trim()) return 'travel';
  return '';
}

function purchaseLogo(transaction) {
  const merchant = transaction.merchant || {};
  return String(
    transaction.merchant_logo_url ||
      transaction.logo_url ||
      transaction.logoUrl ||
      transaction.small_logo_url ||
      merchant.logo_url ||
      merchant.small_logo_url ||
      ''
  ).trim();
}

// Stripe checkout writes a core transaction next to its ledger row; the ledger row carries the reward.
function isStripeCheckoutTransaction(transaction) {
  return transaction?.type === 'stripe' || String(transaction?.transaction_id || '').startsWith('stripe_checkout_');
}

export function purchaseEntries(payload) {
  const raw = payload?.transactions || payload?.results || payload;
  if (!Array.isArray(raw)) return [];
  return raw.map((transaction, index) => {
    const stripe = isStripeTransaction(transaction);
    const dollars = transaction.amount == null ? null : Number(transaction.amount);
    return {
      id: `purchase-${transaction.id || transaction.transaction_id || index}`,
      source: purchaseSource(transaction),
      logoUrl: purchaseLogo(transaction),
      date:
        transaction.transaction_date ||
        transaction.transactionDate ||
        transaction.date ||
        transaction.created_at ||
        transaction.createdAt ||
        null,
      display_title: stripe
        ? stripeRewardTitle(transaction) || 'Card purchase'
        : transaction.display_title ||
          transaction.merchant_name ||
          transaction.wildfire_merchant_name ||
          transaction.travel_merchant_name ||
          'Purchase',
      points: transaction.points_earned != null ? transaction.points_earned : transaction.points,
      entries_earned: entriesOf(transaction),
      // Ledger rows carry no amount; only a purchase with one gets the money line, as in the embed.
      amount: dollars,
      channel: purchaseChannel(transaction),
      activity_kind: stripe ? 'stripe_card_purchase' : undefined,
      redemption: stripe && Number.isFinite(dollars)
        ? { payment_method: 'stripe', amount_paid_cents: Math.round(dollars * 100) }
        : undefined,
    };
  });
}

// A ledger row written for a purchase; the transactions feed lists that purchase itself.
// A Wildfire refund (negative points) is not one: the feed has no row for it, so it stays listed.
function isPurchaseLedgerEntry(entry) {
  if (Number(entry?.points) < 0 && String(entry?.source || '').toLowerCase() === 'wildfire') return false;
  if (entry?.metadata?.transaction_id) return true;
  const source = String(entry?.source || '').toLowerCase();
  if (source === 'olive' || source === 'wildfire' || source === 'travel') return true;
  const earning = String(entry?.earning_source || '').toLowerCase();
  return earning === 'transaction' || earning === 'commission';
}

export const ACTIVITY_LEDGER_LIMIT = 50;

const STRIPE_CHECKOUT_PREFIX = 'stripe_checkout_';

// Stripe session ids of a checkout feed row; a merged row ("Homecrowd (2 items)") lists every part.
function checkoutSessionIds(transaction) {
  const ids =
    Array.isArray(transaction.merged_transaction_ids) && transaction.merged_transaction_ids.length
      ? transaction.merged_transaction_ids
      : [transaction.transaction_id];
  return ids.map((id) => String(id || '').replace(STRIPE_CHECKOUT_PREFIX, ''));
}

function cardLedgerSessionId(entry) {
  return entry?.activity_kind === 'stripe_card_purchase' ? entry.metadata?.stripe_checkout_session_id || null : null;
}

// A checkout feed row is paired, and dropped, only when every one of its session ids has a loaded
// ledger row of the same session; each ledger row pairs once. Otherwise the feed row stays listed.
export function unpairedFeedRows(feed, ledger) {
  const available = new Set((Array.isArray(ledger) ? ledger : []).map(cardLedgerSessionId).filter(Boolean));
  const paired = new Set();
  feed.filter(isStripeCheckoutTransaction).forEach((transaction) => {
    const ids = checkoutSessionIds(transaction);
    if (!ids.every((id) => id && available.has(id))) return;
    ids.forEach((id) => available.delete(id));
    paired.add(transaction);
  });
  return feed.filter((transaction) => !paired.has(transaction));
}

// The activity log with the program on: every purchase of the transactions feed (what Home
// lists, whether or not it wrote a points row) and the ledger rows that are not purchases.
// A reward bought by card is listed once, by its ledger row, when that row is loaded and pairs
// with the feed row. A failed feed leaves the ledger as it was.
export function mergeActivity(ledger, transactionsPayload) {
  const feed = transactionsPayload?.transactions || transactionsPayload?.results || transactionsPayload;
  if (!Array.isArray(feed)) return visibleDreamActivity(ledger, true);
  const others = (Array.isArray(ledger) ? ledger : []).filter((entry) => !isPurchaseLedgerEntry(entry));
  const purchases = purchaseEntries(unpairedFeedRows(feed, ledger));
  return [...purchases, ...others].sort((a, b) => activityTime(b) - activityTime(a));
}
